import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { partnerDashboardSliceAction } from "../../slices/partner-dashboard";
import type { PartnerDashboardAPIResponse } from "../../slices/partner-dashboard/Types";
import {
  getPartnerDashboardSagaAction,
  partnerDashboardSagaActionTypes,
  sendContractReminderSagaAction,
  updatePartnerSupportLevelSagaAction,
} from "./saga-actions";

type UpdateSupportLevelAPIResponse = {
  success: boolean;
  message?: string;
};

type SendContractReminderAPIResponse = {
  success: boolean;
  message?: string;
};

const getContractReminderErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { message?: string } } })
      .response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }
  return error instanceof Error
    ? error.message
    : "Failed to send contract reminder.";
};

const getPartnerDashboardAPI = async (partnerId: number) => {
  const response = await axiosRestInstance.get<PartnerDashboardAPIResponse>(
    `/dashboard/partner/${partnerId}`
  );
  return response.data;
};

const updatePartnerSupportLevelAPI = async (
  partnerId: number,
  payload: { support_level: string; support_status: number }
) => {
  const response = await axiosRestInstance.put<UpdateSupportLevelAPIResponse>(
    `/supportLevel/${partnerId}`,
    payload
  );
  return response.data;
};

const sendContractReminderAPI = async (partnerId: number) => {
  const response = await axiosRestInstance.put<SendContractReminderAPIResponse>(
    `/contractReminder/${partnerId}`
  );
  return response.data;
};

function* getPartnerDashboardSaga(
  action: ReturnType<typeof getPartnerDashboardSagaAction>
) {
  try {
    yield put(partnerDashboardSliceAction.setPartnerDashboardLoading(true));
    yield put(partnerDashboardSliceAction.setPartnerDashboardError(null));

    const data: PartnerDashboardAPIResponse = yield call(
      getPartnerDashboardAPI,
      action.partnerId
    );

    yield put(
      partnerDashboardSliceAction.setPartnerDashboardData({
        partnerId: action.partnerId,
        data,
      })
    );
  } catch (error) {
    console.error(error);
    yield put(partnerDashboardSliceAction.clearPartnerDashboard());
    yield put(
      partnerDashboardSliceAction.setPartnerDashboardError(
        "Unable to load partner dashboard data."
      )
    );
  } finally {
    yield put(partnerDashboardSliceAction.setPartnerDashboardLoading(false));
  }
}

function* updatePartnerSupportLevelSaga(
  action: ReturnType<typeof updatePartnerSupportLevelSagaAction>
) {
  const { partnerId, support_level, support_status } = action.payload;

  try {
    yield put(partnerDashboardSliceAction.setSupportStatusSaving(true));
    yield put(partnerDashboardSliceAction.setSupportStatusError(null));

    const response: UpdateSupportLevelAPIResponse = yield call(
      updatePartnerSupportLevelAPI,
      partnerId,
      { support_level, support_status }
    );

    if (response?.success) {
      yield put(
        partnerDashboardSliceAction.updatePartnerSupportStatus({
          support_level,
          support_status,
        })
      );
    } else {
      yield put(
        partnerDashboardSliceAction.setSupportStatusError(
          response?.message ?? "Failed to update support status."
        )
      );
    }
  } catch (error) {
    yield put(
      partnerDashboardSliceAction.setSupportStatusError(
        error instanceof Error
          ? error.message
          : "Failed to update support status."
      )
    );
  } finally {
    yield put(partnerDashboardSliceAction.setSupportStatusSaving(false));
  }
}

function* sendContractReminderSaga(
  action: ReturnType<typeof sendContractReminderSagaAction>
) {
  const { partnerId } = action.payload;

  try {
    yield put(partnerDashboardSliceAction.setContractReminderLoading(true));

    const response: SendContractReminderAPIResponse = yield call(
      sendContractReminderAPI,
      partnerId
    );

    if (response?.success) {
      yield put(
        partnerDashboardSliceAction.setContractReminderResult({
          status: "success",
          message: response.message ?? "Reminder sent successfully.",
        })
      );
    } else {
      yield put(
        partnerDashboardSliceAction.setContractReminderResult({
          status: "error",
          message: response?.message ?? "Failed to send contract reminder.",
        })
      );
    }
  } catch (error) {
    yield put(
      partnerDashboardSliceAction.setContractReminderResult({
        status: "error",
        message: getContractReminderErrorMessage(error),
      })
    );
  } finally {
    yield put(partnerDashboardSliceAction.setContractReminderLoading(false));
  }
}

function* partnerDashboardSaga() {
  yield takeLatest(
    partnerDashboardSagaActionTypes.callGetPartnerDashboardSaga,
    getPartnerDashboardSaga
  );
  yield takeLatest(
    partnerDashboardSagaActionTypes.callUpdatePartnerSupportLevelSaga,
    updatePartnerSupportLevelSaga
  );
  yield takeLatest(
    partnerDashboardSagaActionTypes.callSendContractReminderSaga,
    sendContractReminderSaga
  );
}

export default partnerDashboardSaga;
